import { useState } from 'react';
import Dropdown from './Dropdown';
import Field from './Field';
import Icon from './Icon';
import Modal from './Modal';
import StateCityFields, {
  EMPTY_ADDRESS,
  OTHER_CITY,
  addressPayload,
  isOtherCity,
} from './StateCityFields';
import { ErrorBanner } from './Feedback';
import useForm from '../hooks/useForm';
import * as v from '../validation/rules';
import { REGISTRATION_CODES } from '../validation/serverCodes';
import { api } from '../api/client';

/**
 * Correcting an account, and setting a password on one.
 *
 * <p>Both screens that list people need both, and both lists hold the same kind of
 * record whatever the role — so these live here rather than twice over in Internal
 * Users and Registered Users, where the second copy is the one that misses a field.
 *
 * <p>What is editable is what every role has: a name, a number, an address. A
 * teacher's school, subjects and verification are Teacher Verification's, which is a
 * different decision with a different audit trail, and putting them on a general edit
 * form would make a correction look like an approval.
 */

const SALUTATIONS = [
  { value: '', label: 'None' },
  { value: 'MR', label: 'Mr.' },
  { value: 'MRS', label: 'Mrs.' },
  { value: 'MS', label: 'Ms.' },
  { value: 'DR', label: 'Dr.' },
];

/**
 * The address as the picker holds it, from the account as the API returns it.
 *
 * <p>A city that came from the master seeds both halves. A typed one — the form's
 * "Other" — has a name and no id, so it re-opens on Other with the text in the box,
 * rather than looking like no city at all and being saved away.
 */
export function addressOf(user) {
  if (!user) return EMPTY_ADDRESS;
  const state = user.stateId ? { id: user.stateId, name: user.state } : null;
  if (user.cityId) {
    return { state, city: { id: user.cityId, name: user.city }, cityName: '' };
  }
  if (user.city) {
    return { state, city: { id: OTHER_CITY, name: 'Other — not in the list' }, cityName: user.city };
  }
  return { state, city: null, cityName: '' };
}

/** @param user the account being corrected, as the admin list returned it */
export function UserEditModal({ user, onClose, onSaved }) {
  const [address, setAddress] = useState(() => addressOf(user));
  const [cityError, setCityError] = useState(null);
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: {
      salutation: user.salutation ?? '',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      mobile: user.mobile ?? '',
      email: user.email ?? '',
      area: user.area ?? '',
      postalCode: user.postalCode ?? '',
    },
    // The registration form's own rules, so a name that is too long is refused the
    // same way whichever screen typed it. postalCode and area are already optional.
    validators: {
      firstName: v.firstName,
      lastName: v.lastName,
      mobile: v.mobile,
      email: v.email,
      area: v.area,
      postalCode: v.postalCode,
    },
  });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    // Other with nothing typed in it is not an address; the server would store a null
    // city and the form would re-open claiming the city was never chosen.
    const gap = isOtherCity(address) && !address.cityName.trim() ? 'Enter the city name' : null;
    setCityError(gap);
    if (gap) return;
    try {
      const saved = await api.adminUpdateUser(user.id, {
        salutation: values.salutation || null,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim() || null,
        mobile: values.mobile.trim(),
        email: values.email.trim(),
        area: values.area.trim() || null,
        postalCode: values.postalCode.trim() || null,
        ...addressPayload(address),
      });
      await onSaved(saved);
    } catch (err) {
      if (err?.code === 'invalid_city') {
        setCityError(err.message);
        return;
      }
      if (!form.applyServerErrors(err, REGISTRATION_CODES)) setError(err);
    }
  });

  return (
    <Modal
      title={`Edit ${user.fullName}`}
      subtitle={`${user.roleLabel} · the account record, not their verification`}
      width={620}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-user" className="btn btn-primary" disabled={form.submitting}>
            <Icon name="check" size={15} />
            {form.submitting ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-user" className="stack" {...form.formProps} onSubmit={submit}>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        <div className="grid-3 grid-3-lead-narrow">
          <Field label="Salutation" id="salutation">
            <Dropdown
              id="salutation"
              options={SALUTATIONS}
              value={form.values.salutation}
              onChange={(salutation) => form.setValue('salutation', salutation)}
              placeholder="None"
            />
          </Field>
          <Field label="First name" id="firstName" required error={form.errorFor('firstName')}>
            <input className="input" maxLength={60} {...form.field('firstName')} />
          </Field>
          <Field label="Last name" id="lastName" error={form.errorFor('lastName')}>
            <input className="input" maxLength={60} {...form.field('lastName')} />
          </Field>
        </div>

        <div className="grid-2">
          <Field
            label="Mobile"
            id="mobile"
            required
            error={form.errorFor('mobile')}
            hint={user.role === 'STUDENT' ? "The parent's number, which is what signs in" : undefined}
          >
            <input className="input" inputMode="numeric" maxLength={10} {...form.field('mobile')} />
          </Field>
          <Field label="Email" id="email" required error={form.errorFor('email')}>
            <input className="input" type="email" maxLength={255} {...form.field('email')} />
          </Field>
        </div>

        <Field label="Area" id="area" error={form.errorFor('area')}>
          <input className="input" maxLength={120} {...form.field('area')} />
        </Field>

        <StateCityFields value={address} onChange={setAddress} cityError={cityError} />

        <Field label="Postal code" id="postalCode" error={form.errorFor('postalCode')}>
          <input className="input" inputMode="numeric" maxLength={6} {...form.field('postalCode')} />
        </Field>
      </form>
    </Modal>
  );
}

/**
 * Setting a password on somebody else's account.
 *
 * <p>Two ways, because the ordinary one has a failure mode this has to survive:
 * generating a password and emailing it is right when the mailbox works, and useless
 * when the relay is down or the address on the account is the one the person has lost
 * access to. Typing one lets the administrator read it out instead.
 *
 * <p>Either way it signs every device out, which the dialogue says before it is done
 * rather than after — the person on the other end is about to be logged out of a
 * lesson, and an administrator ought to know that when they decide.
 */
export function ResetPasswordModal({ user, onClose, onDone }) {
  const [mode, setMode] = useState('generate');
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: { password: '' },
    // Only a typed password is checked here; a generated one is the server's and
    // already satisfies the policy by construction.
    validators: { password: (value) => (mode === 'set' ? v.password(value) : null) },
  });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await api.adminResetPassword(user.id, mode === 'set' ? values.password : '');
      await onDone(result);
    } catch (err) {
      if (!form.applyServerErrors(err, { weak_password: 'password' })) setError(err);
    }
  });

  return (
    <Modal
      title={`Reset password · ${user.fullName}`}
      subtitle="Not a recovery — this overrides the password, and the holder is not asked"
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="reset-password" className="btn btn-danger" disabled={form.submitting}>
            {form.submitting ? 'Resetting…' : 'Reset password'}
          </button>
        </>
      }
    >
      <form id="reset-password" className="stack" {...form.formProps} onSubmit={submit}>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        <p className="small muted" style={{ margin: 0 }}>
          Every device {user.fullName} is signed in on will be signed out, and they will be
          asked to choose a new password at their next sign-in.
        </p>

        <div className="seg">
          <label className="seg-opt">
            <input
              type="radio"
              name="resetmode"
              checked={mode === 'generate'}
              onChange={() => setMode('generate')}
            />
            <span>Generate and email it</span>
          </label>
          <label className="seg-opt">
            <input
              type="radio"
              name="resetmode"
              checked={mode === 'set'}
              onChange={() => setMode('set')}
            />
            <span>Set one now</span>
          </label>
        </div>

        {mode === 'generate' ? (
          <p className="hint" style={{ margin: 0 }}>
            Sent to {user.email ?? 'the address on the account'}. If email is not configured in
            this environment, nothing reaches them — set one instead and pass it on yourself.
          </p>
        ) : (
          <Field
            label="New password"
            id="password"
            required
            error={form.errorFor('password')}
            hint="At least 8 characters, including one uppercase letter and one number. Read it out; it is not shown again."
          >
            <input
              className="input"
              type="text"
              autoComplete="off"
              maxLength={100}
              {...form.field('password')}
            />
          </Field>
        )}
      </form>
    </Modal>
  );
}
